// Print a lab report.
//
// The on-screen modal is a working surface — status pills, the observation
// editor, the ABHA panel — none of which belongs on paper. So the PDF is built
// from the data rather than captured from that DOM: the same identification box
// and letterhead the radiology report and the discharge summary carry, then one
// block per test.
//
// The page box, typography and page breaking live in utils/reportSheet.js; the
// header/footer stamped onto every page in utils/reportLetterhead.js.
import { toast } from "react-toastify";
import {
  buildIdBox,
  esc,
  formatAgeGender,
  formatReportDate,
  renderReportSheet,
} from "../../../utils/reportSheet";
import { printPdfBlob } from "../../../utils/printPdfBlob";
import { resolveFacilityDetails, resolveLogo } from "../../../utils/reportLetterhead";
import {
  formatSignatoryNames,
  resolveReportSignatories,
} from "../../diagnostic-reports/diagnosticReportApi";

const REFERENCE_COLUMNS = ["Parameter", "Result", "Unit", "Reference Range"];

// One test: its heading, then either the parameter table or a note explaining
// why there is none.
//
// Nothing sits between the two. Order number, order date, referring doctor and
// technician are already stated once in the identification box at the top — on
// a single-test report they were printing twice, one line apart — and the CPT
// is a billing code, not something the patient's copy needs to carry.
const testBlock = (order, index) => {
  const rows = Array.isArray(order?.rows) ? order.rows : [];

  let body;
  if (rows.length > 0) {
    body = `
      <table>
        <tbody>
          <tr>${REFERENCE_COLUMNS.map((c) => `<th>${c}</th>`).join("")}</tr>
          ${rows
            .map(
              (r) => `<tr>
                <td>${esc(r?.param)}</td>
                <td><strong>${esc(r?.value)}</strong></td>
                <td>${esc(r?.unit || "-")}</td>
                <td>${esc(r?.refRange || "-")}</td>
              </tr>`,
            )
            .join("")}
        </tbody>
      </table>`;
  } else {
    // Deliberately says nothing about the workflow stage — that belongs on the
    // orders dashboard, not on the document handed to a patient.
    body = `<p><em>Results awaited.</em></p>`;
  }

  return `
    <h3>${index + 1}. ${esc(order?.service)}</h3>
    ${body}
    ${
      order?.interpretation
        ? `<p><strong>Interpretation:</strong><br/>${esc(order.interpretation).replace(/\n/g, "<br/>")}</p>`
        : ""
    }
  `;
};

// Names arrive bare ("Sruti Mehra"). The guard keeps a feed that already sends
// a title from stuttering into "Dr. Dr.". Same rule the radiology report
// applies (radiologyOrdersShared.withSalutation).
export const withDoctorPrefix = (value) => {
  const name = String(value || "").trim();
  if (!name || name === "-") return "";
  return /^(dr|doctor|prof|mr|mrs|ms|mx)\b\.?/i.test(name) ? name : `Dr. ${name}`;
};

// The referring doctor reaches the report on the order ("doctor"), but the bulk
// lab list often leaves that blank and names the doctor once per admission
// instead — which is why "Ref. By Doctor" printed a bare "-". Fall back to the
// admission's doctor before giving up.
export const resolveReferringDoctor = (primary, patient) => {
  const name = [primary?.doctor, patient?.doctorName, patient?.doctor]
    .map((v) => String(v || "").trim())
    .find((v) => v && v !== "-");
  return withDoctorPrefix(name);
};

// Bed, with the ward/room line beside it when the admission carries one — same
// layout as the radiology report identification box.
export const formatLabReportBed = (patient) => {
  const bed =
    patient?.bedNo && patient.bedNo !== "-"
      ? String(patient.bedNo).trim()
      : "";
  const room =
    patient?.roomNo && patient.roomNo !== "-"
      ? String(patient.roomNo).trim()
      : "";
  if (bed && room) return `${bed} · ${room}`;
  return bed || room || "-";
};

export function buildLabReportHtml({ patient, orders, generatedAt }) {
  const list = Array.isArray(orders) ? orders : [];
  const primary = list[0] || {};

  return `
    <div class="rp-title">Laboratory Report</div>
    ${buildIdBox(
      [
        ["Patient Name", patient?.name],
        ["Patient ID", patient?.uhid],
        ["Age / Gender", formatAgeGender(patient?.age, patient?.gender)],
        ["Bed", formatLabReportBed(patient)],
        ["Ref. By Doctor", resolveReferringDoctor(primary, patient)],
        ["Department", primary?.department],
      ],
      [
        ["Order No.", primary?.orderNo],
        ["Order Date", formatReportDate(primary?.ordered)],
        ["Report Date", formatReportDate(generatedAt || Date.now())],
        ["Total Tests", String(list.length)],
      ],
    )}
    <div class="rp-body">
      ${list.length ? list.map(testBlock).join("") : "<p>No tests on this report.</p>"}
    </div>
  `;
}

/**
 * Render the lab report to a PDF Blob.
 *
 * @param {object}  args
 * @param {object}  args.patient    { name, uhid, age, gender, dob }
 * @param {Array}   args.orders     one entry per test, already resolved by the
 *   caller: { orderNo, service, cpt, ordered, doctor, technician, status,
 *   pending, rows: [{param, value, unit, refRange}], interpretation }
 * @param {string}  args.technician      salutation + name as typed on the report
 *   screen. Prints over the "Prepared By" rule in the letterhead footer.
 * @param {Array}   args.brandingData    BrandingContext value — the facility's
 *   configurable logo. Omit and the bundled logo is used.
 * @param {object}  args.userInfo        PermissionContext's `userInfo` — supplies
 *   the facility address, the same source the billing invoice prints from.
 * @param {boolean} args.withLetterhead  false prints the content alone, for
 *   hospitals feeding pre-printed stationery through the tray.
 * @param {boolean} args.autoPrint       ask the viewer to open its print dialog.
 */
export async function generateLabReportPdf({
  patient,
  orders,
  generatedAt,
  technician,
  brandingData,
  userInfo,
  withLetterhead = true,
  autoPrint = false,
  patientLabTestId,
  signatories,
}) {
  const safeName = String(patient?.name || "Patient").replace(/[^a-zA-Z0-9]/g, "_");
  const { address, contact } = resolveFacilityDetails(userInfo);
  const primary = (Array.isArray(orders) ? orders : [])[0] || {};
  // Typed on the report screen; falls back to whatever the order carried for
  // hospitals that get it from the backend instead.
  const preparedBy = withDoctorPrefix(technician || primary.technician);
  const resolvedSignatories = await resolveReportSignatories({
    signatories,
    patientLabTestId: patientLabTestId || primary.patientLabTestId,
    extraIds: (Array.isArray(orders) ? orders : []).map(
      (o) => o.patientLabTestId || o.patient_lab_test_id
    ),
    roleType: "pathologist",
  });
  const hasSignatureImage = resolvedSignatories.some((s) => s.image);

  return renderReportSheet({
    html: buildLabReportHtml({ patient, orders, generatedAt }),
    filename: `Lab_Report_${safeName}_${primary.orderNo || ""}.pdf`,
    autoPrint,
    bottomMm: hasSignatureImage ? 44 : 30,
    letterhead: withLetterhead
      ? {
          logoBase64: resolveLogo(brandingData),
          address,
          contact,
          patientId: patient?.uhid,
          preparedBy,
          checkedBy: formatSignatoryNames(resolvedSignatories),
          checkedSignatures: resolvedSignatories,
        }
      : null,
  });
}

/**
 * Save the report as a file. Deliberately separate from printLabReport: that
 * one opens a viewer tab with autoPrint set, which is a print action, not a
 * download — wiring the Download button to it made the two buttons do the same
 * thing.
 */
export async function downloadLabReport(args) {
  try {
    const blob = await generateLabReportPdf({ ...args, autoPrint: false });
    const safeName = String(args?.patient?.name || "Patient").replace(
      /[^a-zA-Z0-9]/g,
      "_",
    );
    const orderNo = (Array.isArray(args?.orders) ? args.orders : [])[0]?.orderNo;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Lab_Report_${safeName}_${orderNo || ""}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Same reason as below: revoking synchronously can abort the save in some
    // browsers before it has read the blob.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    console.error("Could not generate the lab report PDF:", err);
    toast.error("Could not download the report PDF. Please try again.");
  }
}

export async function printLabReport(args) {
  try {
    const blob = await generateLabReportPdf({ ...args, autoPrint: false });
    try {
      await printPdfBlob(blob);
    } catch (printErr) {
      console.warn("iframe print failed, trying new tab:", printErr);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        URL.revokeObjectURL(url);
        toast.error("Could not open the print dialog. Try Download instead.");
        return;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  } catch (err) {
    console.error("Could not generate the lab report PDF:", err);
    toast.error("Could not generate the report PDF. Please try again.");
  }
}
